/**
 * AI Coach API — five-agent service.
 *
 * Flow: Web Portal → five-agent (coach API) → five-context-conversation-service (Java).
 * API base: /api/agent/api/v1/coach (proxy to five-agent at :18000).
 *
 * Session: POST /session/start → POST /session/end.
 * Chat: WebSocket /chat/ws — { sessionId, userId, message, chatHistory: [] } (first)
 *       or { sessionId, userId, chatHistory } (follow-ups). Events: stream_start → token* → stream_end
 *       → sources → follow_ups → evaluation → telemetry → done.
 *
 * Aligned with five-context-conversation-service API spec:
 * - GET /conversations/{userId} (list), /status/{status}, /folder/{folderId}
 * - GET /conversations/{id}/messages (page, size)
 * - PUT /conversations/{id} (update), agent exposes /title convenience
 * - DELETE /conversations/{id}
 * - GET /conversations/search?userId=&query=
 */

import type {
  SessionStartRequest,
  SessionStartResponse,
  SessionEndRequest,
  SessionEndResponse,
  CoachGoalsResponse,
  CoachNudgeResponse,
  CoachFeedbackResponse,
  CoachVerifyGenerateResponse,
  CoachVerifyEvaluateResponse,
  CoachFolder,
  ActivitySummaryResponse,
  CoachSummarizeResponse,
  ConversationsListResponse,
  ConversationMessagesResponse,
} from '@/types/coach';

const COACH_BASE = '/api/agent/api/v1/coach';

// Same key used by ApiService (api.ts) and AuthContext — both read/write here.
const TOKEN_STORAGE_KEY = 'five_ai_access_token';

function getBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

/** Read the current user's JWT from localStorage (set by AuthContext or AdminAuthContext). */
function getAuthHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function coachFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${getBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
      ...options.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // five-agent returns { detail } (FastAPI default) or { error } (custom responses)
    const b = body as { detail?: string; error?: string };
    const message = b.detail ?? b.error ?? res.statusText;
    throw new Error(message || `Coach API ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const coachApi = {
  /** Start a coaching session. Returns conversationId and status. */
  startSession(body: SessionStartRequest): Promise<SessionStartResponse> {
    return coachFetch<SessionStartResponse>(`${COACH_BASE}/session/start`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** End session; optional triggerRecalc for intelligence. */
  endSession(body: SessionEndRequest): Promise<SessionEndResponse> {
    return coachFetch<SessionEndResponse>(`${COACH_BASE}/session/end`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Fire-and-forget session end for beforeunload / page close.
   * Uses keepalive so the request completes even when the tab is closing.
   */
  endSessionOnPageUnload(body: SessionEndRequest): void {
    if (typeof window === 'undefined') return;
    const url = `${getBaseUrl()}${COACH_BASE}/session/end`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ ...body, triggerRecalc: true }),
      keepalive: true,
    }).catch(() => {});
  },

  /** Get all folders for user. */
  getFolders(userId: string): Promise<CoachFolder[]> {
    return coachFetch<CoachFolder[]>(`${COACH_BASE}/folders/${encodeURIComponent(userId)}`);
  },

  /** Create folder. (Spec: description optional.) */
  createFolder(body: { userId: string; name: string; description?: string; color?: string; icon?: string }): Promise<CoachFolder> {
    return coachFetch<CoachFolder>(`${COACH_BASE}/folders`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** Update folder. (Spec: name, description, color, icon optional.) */
  updateFolder(folderId: string, body: { name?: string; description?: string; color?: string; icon?: string }): Promise<CoachFolder> {
    return coachFetch<CoachFolder>(`${COACH_BASE}/folders/${encodeURIComponent(folderId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  /** Delete folder. */
  deleteFolder(folderId: string): Promise<{ deleted: boolean }> {
    return coachFetch<{ deleted: boolean }>(`${COACH_BASE}/folders/${encodeURIComponent(folderId)}`, {
      method: 'DELETE',
    });
  },

  /** Move conversation to folder. */
  moveConversation(body: { userId: string; conversationId: string; folderId: string }): Promise<unknown> {
    return coachFetch(`${COACH_BASE}/conversations/move`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** Get personalized goals (short-term + long-term). */
  getGoals(userId: string): Promise<CoachGoalsResponse> {
    return coachFetch<CoachGoalsResponse>(`${COACH_BASE}/goals`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  },

  /** Get daily nudge. */
  getNudge(userId: string, nudgeType: string = 'encouragement'): Promise<CoachNudgeResponse> {
    return coachFetch<CoachNudgeResponse>(`${COACH_BASE}/nudge`, {
      method: 'POST',
      body: JSON.stringify({ userId, nudgeType }),
    });
  },

  /** Get engagement feedback. */
  getFeedback(userId: string, timeframeDays: number = 14): Promise<CoachFeedbackResponse> {
    return coachFetch<CoachFeedbackResponse>(`${COACH_BASE}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ userId, timeframeDays }),
    });
  },

  /** Generate quiz questions. */
  verifyGenerate(body: {
    sessionId: string;
    userId: string;
    topic: string;
    numQuestions?: number;
    difficulty?: string;
  }): Promise<CoachVerifyGenerateResponse> {
    return coachFetch<CoachVerifyGenerateResponse>(`${COACH_BASE}/verify`, {
      method: 'POST',
      body: JSON.stringify({ ...body, numQuestions: body.numQuestions ?? 3 }),
    });
  },

  /** Evaluate quiz answers. */
  verifyEvaluate(body: {
    sessionId: string;
    userId: string;
    topic: string;
    userAnswers: Record<string, string>;
  }): Promise<CoachVerifyEvaluateResponse> {
    return coachFetch<CoachVerifyEvaluateResponse>(`${COACH_BASE}/verify`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** Activity summary (narrative + extracted facts). */
  getActivitySummary(userId: string): Promise<ActivitySummaryResponse> {
    return coachFetch<ActivitySummaryResponse>(`${COACH_BASE}/activity-summary/${encodeURIComponent(userId)}`);
  },

  /** On-demand conversation summarization (stores in Redis + conversation service). */
  summarize(body: { sessionId: string; userId: string }): Promise<CoachSummarizeResponse> {
    return coachFetch<CoachSummarizeResponse>(`${COACH_BASE}/summarize`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** Trigger intelligence recalculation. */
  recalculateIntelligence(): Promise<unknown> {
    return coachFetch(`${COACH_BASE}/recalculate-intelligence`, { method: 'POST' });
  },

  /**
   * List conversations for a user.
   * five-agent: GET /conversations/{userId}?status=&folder_id=&limit=
   * Maps to context-conversation-service: /status/{status} or /folder/{folderId} or base list.
   */
  async getConversations(
    userId: string,
    params?: { limit?: number; offset?: number; search?: string; status?: 'ACTIVE' | 'CLOSED'; folderId?: string }
  ): Promise<ConversationsListResponse> {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.offset != null) sp.set('offset', String(params.offset));
    if (params?.search) sp.set('search', params.search);
    if (params?.status) sp.set('status', params.status);
    if (params?.folderId) sp.set('folder_id', params.folderId);
    const qs = sp.toString();
    const data = await coachFetch<ConversationsListResponse & { content?: unknown[]; results?: unknown[] }>(
      `${COACH_BASE}/conversations/${encodeURIComponent(userId)}${qs ? `?${qs}` : ''}`
    );
    // Agent returns { conversations }; Java/Spring may use { content }; search uses { results }
    const list = data.conversations ?? data.content ?? data.results ?? [];
    return { conversations: list, total: list.length, limit: params?.limit ?? 50, offset: params?.offset ?? 0 };
  },

  /**
   * Search conversations by title (dedicated endpoint per agent API).
   * five-agent: GET /conversations/{userId}/search?q=&limit=
   * Returns { results } (agent schema) — normalized to { conversations } for callers.
   */
  async searchConversations(userId: string, query: string, limit = 10): Promise<ConversationsListResponse> {
    const qs = new URLSearchParams();
    qs.set('q', query);
    if (limit > 0) qs.set('limit', String(limit));
    const data = await coachFetch<ConversationsListResponse & { results?: ConversationsListResponse['conversations'] }>(
      `${COACH_BASE}/conversations/${encodeURIComponent(userId)}/search?${qs.toString()}`
    );
    // Agent returns results; list returns conversations
    const list = data.results ?? data.conversations ?? [];
    return { conversations: list, total: list.length, limit, offset: 0 };
  },

  /**
   * Update conversation title.
   * five-agent: PUT /conversations/{id}/title body { userId, title }
   * Maps to context-conversation-service PUT /conversations/{id}?userId= with { title }.
   */
  updateConversationTitle(conversationId: string, body: { userId: string; title: string }): Promise<{ conversation_id: string; title: string }> {
    return coachFetch(`${COACH_BASE}/conversations/${encodeURIComponent(conversationId)}/title`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  /**
   * Soft-delete a conversation.
   * five-agent: DELETE /conversations/{id}?userId=
   * context-conversation-service: DELETE /conversations/{id}?userId= (mandatory).
   */
  deleteConversation(conversationId: string, userId: string): Promise<{ deleted: boolean }> {
    const params = new URLSearchParams({ userId });
    return coachFetch(
      `${COACH_BASE}/conversations/${encodeURIComponent(conversationId)}?${params}`,
      { method: 'DELETE' }
    );
  },

  /**
   * Get messages for a conversation (restore on refresh).
   * five-agent: GET /conversations/{id}/messages?limit=
   * context-conversation-service: GET /conversations/{id}/messages?size=
   * Response includes userId per message. Messages saved async — use local state as primary.
   */
  getConversationMessages(conversationId: string, limit = 50): Promise<ConversationMessagesResponse> {
    return coachFetch<ConversationMessagesResponse>(
      `${COACH_BASE}/conversations/${encodeURIComponent(conversationId)}/messages?limit=${limit}`
    );
  },
};

/**
 * Build WebSocket URL for streaming chat.
 * Appends ?token=<jwt> so five-agent can authenticate the browser connection
 * (browsers cannot send custom Authorization headers on WebSocket).
 */
export function getCoachWsUrl(): string {
  // Explicit direct URL (e.g. ws://localhost:18000/api/v1/coach/chat/ws) when proxy doesn't upgrade
  const direct = import.meta.env.VITE_FIVE_AGENT_WS_URL;
  const token = typeof window !== 'undefined' ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
  const suffix = token ? `?token=${encodeURIComponent(token)}` : '';
  if (direct && typeof direct === 'string') return `${direct}${suffix}`;
  const base = getBaseUrl();
  const protocol = base.startsWith('https') ? 'wss' : 'ws';
  const host = base.replace(/^https?:\/\//, '');
  // Path: /api/agent prefix + backend path (rewrite strips /api/agent → backend gets /api/v1/coach/chat/ws)
  return `${protocol}://${host}/api/agent/api/v1/coach/chat/ws${suffix}`;
}
